import {PatientExtended, PatientPrivacyFormValidationSchema} from '@/features/patients/types'
import {usePatientEditModeView} from '@/features/patients/hooks/usePatientEditModeView'
import {useTranslation} from '@/translations/i18n'
import {PatientDetailsEditButtons} from '@/features/patients/components/patient-details-edit-buttons/PatientDetailsEditButtons'
import {FormProvider, useForm, useFormContext} from 'react-hook-form'
import {PatientPrivacyFields} from '@/features/patients/components/patient-privacy-fields/PatientPrivacyFields'
import {StyledDetailsFieldsSection} from './style'
import {
    getPatientDirtyFieldsFromFormValues,
    remapPatientToDefaultValues,
    remapPatientToPayload
} from '@/features/patients/utils'
import {useOutletContext} from 'react-router-dom'
import {usePatientFormSubmission} from '@/features/patients/hooks/usePatientFormSubmission'
import {useUpdatePatient} from '@/features/patients/services/useUpdatePatient'
import {usePatientDetailsStore} from '@/features/patients/store/usePatientDetailsStore'
import {PatientPrivacyDetails} from '@/features/patients/components/patient-privacy-details/PatientPrivacyDetails'
import {zodResolver} from '@hookform/resolvers/zod'
import {usePermissions} from '@/features/auth/store/store'

export const PatientDetailsPrivacySection = () => {
    const {patient} = useOutletContext<{patient: PatientExtended}>()
    const form = useForm<PatientPrivacyFormValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(PatientPrivacyFormValidationSchema),
        defaultValues: remapPatientToDefaultValues(patient)
    })

    return (
        <FormProvider {...form}>
            <PatientDetailsPrivacySectionContent patient={patient} />
        </FormProvider>
    )
}

const PatientDetailsPrivacySectionContent = ({patient}: {patient: PatientExtended}) => {
    const {t} = useTranslation()
    const {isEditModeEnabled, isDisabledView} = usePatientEditModeView('privacy')
    const permissions = usePermissions()
    const setIsErrorBannerVisible = usePatientDetailsStore(state => state.setIsErrorBannerVisible)
    const form = useFormContext<PatientPrivacyFormValidationSchema>()

    const {onSuccessSubmit, onErrorSubmit, scrollToTop} = usePatientFormSubmission({
        formSchema: PatientPrivacyFormValidationSchema
    })

    // mutation
    const updatePatient = useUpdatePatient({
        urlParams: {patientId: patient.id},
        params: {responseType: 'extended'},
        options: {
            onSuccess: onSuccessSubmit,
            onError: onErrorSubmit
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(
        formValues => {
            const dirtyFormValues = getPatientDirtyFieldsFromFormValues(form.formState.dirtyFields, formValues)
            const parsedValues = remapPatientToPayload(dirtyFormValues)
            return updatePatient.mutate(parsedValues)
        },
        errors => {
            console.error(errors)
            setIsErrorBannerVisible(true)
            scrollToTop()
        }
    )

    return (
        <StyledDetailsFieldsSection
            title={t('commons:privacy')}
            isDisabled={isDisabledView}
            headingEndSlot={
                permissions?.patients?.write && (
                    <PatientDetailsEditButtons
                        groupName="privacy"
                        isConfirmDisabled={!form.formState.isDirty}
                        onCancel={form.reset}
                        onConfirm={handleSubmit}
                    />
                )
            }
        >
            {isEditModeEnabled ? <PatientPrivacyFields /> : <PatientPrivacyDetails patient={patient} />}
        </StyledDetailsFieldsSection>
    )
}
